import { formatDistanceToNow } from 'date-fns'
import { type ChangeEvent, useEffect, useMemo, useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useGetApplicationById } from '@/utils/api/membership-service'
import { FileText, Info, User2 } from 'lucide-react'

import FileViewerDialog from '@/components/common/FileViewerDialog'

import type { AdminApplication } from '../types'
import {
  computeDurationLabel,
  computeRemainingDurationLabel,
  formatCurrencyUGX,
  formatDate,
  formatDateTime,
  formatFullName,
  normaliseApplication,
  parseDate,
} from '../utils'
import { ApplicationStatusBadge } from './ApplicationStatusBadge'
import { ApplicationResponseDTOStatus as ApplicationStatus  } from '@/utils/api/membership-service/models'

interface ApplicationDetailsDrawerProps {
  applicationId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: AdminApplication
  onApprove: (payload: { durationInMonths: number; amount: number }) => Promise<void>
  onReject: (reason: string) => Promise<void>
  onRequestCorrection: (comment: string) => Promise<void>
  isProcessing: boolean
}

export function ApplicationDetailsDrawer({
  applicationId,
  open,
  onOpenChange,
  initialData,
  onApprove,
  onReject,
  onRequestCorrection,
  isProcessing,
}: ApplicationDetailsDrawerProps) {
  const [confirming, setConfirming] = useState<null | 'approve' | 'reject' | 'correction'>(null)
  const [durationValue, setDurationValue] = useState('')
  const [durationUnit, setDurationUnit] = useState<'MONTHS' | 'YEARS'>('YEARS')
  const [durationError, setDurationError] = useState<string | null>(null)
  const [amountValue, setAmountValue] = useState('')
  const [amountError, setAmountError] = useState<string | null>(null)
  const [fileViewerOpen, setFileViewerOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState<{ url: string; name: string } | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const [correctionComment, setCorrectionComment] = useState('')
  const [correctionError, setCorrectionError] = useState<string | null>(null)
  const trimmedDurationValue = durationValue.trim()
  const parsedDurationValue = Number(trimmedDurationValue)
  const durationIsValid =
    trimmedDurationValue.length > 0 &&
    Number.isFinite(parsedDurationValue) &&
    parsedDurationValue > 0 &&
    Number.isInteger(parsedDurationValue)
  const trimmedAmountValue = amountValue.trim()
  const normalizedAmountValue = trimmedAmountValue.replace(/,/g, '')
  const parsedAmountValue =
    normalizedAmountValue.length > 0 ? Number(normalizedAmountValue) : Number.NaN
  const amountIsValid =
    normalizedAmountValue.length > 0 &&
    Number.isFinite(parsedAmountValue) &&
    parsedAmountValue > 0
  const trimmedRejectionReason = rejectionReason.trim()
  const trimmedCorrectionComment = correctionComment.trim()

  const detailQuery = useGetApplicationById<AdminApplication | null>(applicationId, {
    query: {
      enabled: open,
      staleTime: 60_000,
      select: (response) => normaliseApplication(response.data) ?? null,
    },
  })

  const ugxFormatter = useMemo(() => new Intl.NumberFormat('en-UG'), [])

  const application: AdminApplication | null = useMemo(() => {
    return detailQuery.data ?? initialData ?? null
  }, [detailQuery.data, initialData])

  const membershipDuration = useMemo(() => {
    if (!application) return null
    return computeDurationLabel(application.membershipStartAt ?? null, application.membershipExpiryAt ?? null)
  }, [application])

  const remainingDuration = useMemo(() => {
    if (!application) return null
    return computeRemainingDurationLabel(application.membershipExpiryAt ?? null)
  }, [application])

  const submittedDistance = useMemo(() => {
    const submittedAt = application?.submittedAt ?? application?.createdAt
    const parsed = parseDate(submittedAt)
    if (!parsed) return null
    return formatDistanceToNow(parsed, { addSuffix: true })
  }, [application?.submittedAt, application?.createdAt])

  const isPending = application?.status === ApplicationStatus.PENDING
  const isApproved = application?.status === ApplicationStatus.APPROVED
  const isRejected = application?.status === ApplicationStatus.REJECTED

  useEffect(() => {
    if (confirming !== 'approve') {
      setDurationValue('')
      setDurationUnit('YEARS')
      setDurationError(null)
      setAmountValue('')
      setAmountError(null)
    }
  }, [confirming])

  useEffect(() => {
    if (confirming !== 'reject') {
      setRejectionReason('')
      setRejectionError(null)
    }
  }, [confirming])

  useEffect(() => {
    if (confirming !== 'correction') {
      setCorrectionComment('')
      setCorrectionError(null)
    }
  }, [confirming])

  const handleAmountInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value
    const digitsOnly = rawValue.replace(/\D/g, '')

    if (digitsOnly.length === 0) {
      setAmountValue('')
    } else {
      const formatted = ugxFormatter.format(Number(digitsOnly))
      setAmountValue(formatted)
    }

    if (amountError) {
      setAmountError(null)
    }
  }

  const handleApprove = async () => {
    if (trimmedDurationValue.length === 0) {
      setDurationError('Enter the duration the member has paid for.')
      return
    }

    if (!Number.isFinite(parsedDurationValue) || parsedDurationValue <= 0) {
      setDurationError('Provide a positive whole number.')
      return
    }

    if (!Number.isInteger(parsedDurationValue)) {
      setDurationError('Use whole numbers only.')
      return
    }

    const totalMonths = durationUnit === 'YEARS' ? parsedDurationValue * 12 : parsedDurationValue

    if (normalizedAmountValue.length === 0) {
      setAmountError('Enter the amount paid in UGX.')
      return
    }

    if (!Number.isFinite(parsedAmountValue) || parsedAmountValue <= 0) {
      setAmountError('Provide a positive amount.')
      return
    }

    setDurationError(null)
    setAmountError(null)

    try {
      await onApprove({
        durationInMonths: totalMonths,
        amount: parsedAmountValue,
      })
      setConfirming(null)
    } catch (_error) {
      setConfirming(null)
    }
  }

  const handleReject = async () => {
    if (trimmedRejectionReason.length === 0) {
      setRejectionError('Provide a brief reason for the rejection.')
      return
    }

    try {
      await onReject(trimmedRejectionReason)
      setConfirming(null)
    } catch (_error) {
      setConfirming(null)
    }
  }

  const handleRequestCorrection = async () => {
    if (trimmedCorrectionComment.length === 0) {
      setCorrectionError('Provide clear details about what should be corrected.')
      return
    }

    try {
      await onRequestCorrection(trimmedCorrectionComment)
      setConfirming(null)
    } catch (_error) {
      setConfirming(null)
    }
  }

  const handleFileView = (fileUrl: string, fileName: string) => {
    setSelectedFile({ url: fileUrl, name: fileName })
    setFileViewerOpen(true)
  }

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} direction="right">
        <DrawerContent className="w-full sm:w-[90vw] lg:w-[min(50vw,1040px)]">
          <DrawerHeader className="gap-1 border-b">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-2 text-left">
                <DrawerTitle className="flex flex-wrap items-center gap-2 text-lg font-semibold">
                  <User2 className="h-5 w-5 text-muted-foreground" />
                  <span>
                    {application
                      ? formatFullName(application.firstName, application.middleName, application.lastName)
                      : 'Membership application'}
                  </span>
                </DrawerTitle>
                <DrawerDescription className="flex flex-wrap items-center gap-2 text-sm">
                  <span>Application #{application?.id ?? applicationId}</span>
                  {application?.categoryName ? (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <Badge variant="outline">{application.categoryName}</Badge>
                    </>
                  ) : null}
                  {application?.submittedAt ? (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span>
                        Submitted {formatDateTime(application.submittedAt) ?? '—'}
                        {submittedDistance ? ` (${submittedDistance})` : ''}
                      </span>
                    </>
                  ) : null}
                </DrawerDescription>
              </div>
              {application ? <ApplicationStatusBadge status={application.status} /> : null}
            </div>
          </DrawerHeader>

          <ScrollArea className="h-[calc(100vh-10rem)] px-6">
            <div className="flex flex-col gap-6 py-6">
              {detailQuery.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to load application</AlertTitle>
                  <AlertDescription>
                    {detailQuery.error instanceof Error
                      ? detailQuery.error.message
                      : 'We could not retrieve the full application record.'}
                  </AlertDescription>
                </Alert>
              ) : null}

              {!application ? (
                <div className="space-y-4">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : (
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                  <div className="flex-1 space-y-6">
                    <DetailSection
                      title="Membership subscription"
                      description="Active membership details for this approved application."
                    >
                      <DetailGrid
                        rows={[
                          { label: 'Member #', value: application.membershipNumber ?? '—' },
                          { label: 'Start date', value: formatDate(application.membershipStartAt) ?? '—' },
                          { label: 'Expiry date', value: formatDate(application.membershipExpiryAt) ?? '—' },
                          { label: 'Period', value: membershipDuration ?? '—' },
                          { label: 'Remaining', value: remainingDuration ?? '—' },
                          { label: 'Amount paid', value: formatCurrencyUGX(application.membershipAmount) },
                        ]}
                      />
                    </DetailSection>

                    <DetailSection
                      title="Personal details"
                      description="Baseline information provided by the applicant."
                    >
                      <DetailGrid
                        rows={[
                          { label: 'Full name', value: formatFullName(application.firstName, application.middleName, application.lastName) },
                          { label: 'Email', value: application.emailAddress || '—' },
                          { label: 'Phone', value: application.phoneNumbers || '—' },
                          { label: 'Gender', value: application.gender ?? '—' },
                          { label: 'Date of birth', value: formatDate(application.dateOfBirth) ?? '—' },
                          { label: 'Residence', value: application.placeOfResident ?? '—' },
                          { label: 'Organisation', value: application.organisation ?? '—' },
                          {
                            label: 'LinkedIn Profile link',
                            value: application.profileLinkUrl
                              ? (
                                  <a
                                    href={application.profileLinkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary underline underline-offset-4 hover:text-primary/80"
                                  >
                                    {application.profileLinkUrl}
                                  </a>
                                )
                              : '—',
                          },
                        ]}
                      />
                    </DetailSection>

                    <DetailSection
                      title="Application metadata"
                      description="Submission history and processing timestamps."
                    >
                      <DetailGrid
                        rows={[
                          { label: 'Application ID', value: application.id },
                          { label: 'Category', value: application.categoryName || '—' },
                          { label: 'Submitted at', value: formatDateTime(application.submittedAt) ?? '—' },
                          { label: 'Last updated', value: formatDateTime(application.updatedAt) ?? '—' },
                          { label: 'Attachment reference', value: application.attachment ?? '—' },
                        ]}
                      />
                    </DetailSection>

                    <SupplementaryInfo application={application} />

                    <EmploymentHistorySection items={application.employmentHistory} />
                    <EducationHistorySection items={application.education} />
                    <AffiliationsSection items={application.affiliations} />
                    <AwardsSection items={application.awards} />
                    <DocumentsSection
                      items={application?.documents ?? []}
                      onViewFile={handleFileView}
                    />
                  </div>

                  {/* <aside className="space-y-6 lg:w-[360px] xl:w-[420px]">
                    
                  </aside> */}
                </div>
              )}
            </div>
          </ScrollArea>

          <DrawerFooter className="sticky bottom-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            {application ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  {isApproved
                    ? 'This application has been approved. You can revert if necessary from the administration backend.'
                    : isRejected
                      ? 'This application has been rejected. Contact the applicant if re-submission is required.'
                      : 'Review all sections before making a decision.'}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <DrawerClose asChild>
                    <Button variant="outline">Close</Button>
                  </DrawerClose>
                      {(isPending || isRejected) && <Button
                        variant="secondary"
                        disabled={isProcessing}
                        onClick={() => setConfirming('approve')}
                      >
                        {isProcessing && confirming === 'approve' ? 'Approving…' : 'Approve'}
                      </Button>}
                      {(isPending || isRejected) && <Button
                        variant="outline"
                        className="text-foreground hover:text-foreground"
                        disabled={isProcessing}
                        onClick={() => setConfirming('correction')}
                      >
                        {isProcessing && confirming === 'correction' ? 'Requesting…' : 'Request info'}
                      </Button>}
                      {(isPending || isApproved) && <Button
                        variant="destructive"
                        disabled={isProcessing}
                        onClick={() => setConfirming('reject')}
                      >
                        {isProcessing && confirming === 'reject' ? 'Declining…' : 'Decline'}
                      </Button>}
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <DrawerClose asChild>
                  <Button variant="outline">Close</Button>
                </DrawerClose>
              </div>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={confirming === 'approve'} onOpenChange={(open) => setConfirming(open ? 'approve' : null)}>
        <AlertDialogContent className="sm:max-w-xl max-h-[calc(100vh-4rem)] overflow-hidden p-0">
          <div className="flex h-full flex-col">
            <AlertDialogHeader className="space-y-2 px-6 pt-6">
              <AlertDialogTitle>Approve application?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark the application as approved and grant the applicant member access. Record the paid membership term below.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-3 overflow-y-auto px-6 pb-6">
              <div className="space-y-2">
                <Label htmlFor="approval-duration-value">Membership term</Label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="approval-duration-value"
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={durationValue}
                  onChange={(event) => {
                    setDurationValue(event.target.value)
                    if (durationError) {
                      setDurationError(null)
                    }
                  }}
                  placeholder="e.g. 12"
                  className="sm:max-w-[160px]"
                  disabled={isProcessing}
                />
                <Select
                  value={durationUnit}
                  onValueChange={(next) => {
                    setDurationUnit(next as 'MONTHS' | 'YEARS')
                    if (durationError) {
                      setDurationError(null)
                    }
                  }}
                  disabled={isProcessing}
                >
                  <SelectTrigger className="sm:max-w-[140px]">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHS">Months</SelectItem>
                    <SelectItem value="YEARS">Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              The membership duration will be saved in months. For yearly payments we&apos;ll convert the value automatically.
            </p>
            {durationError ? <p className="text-sm text-destructive">{durationError}</p> : null}
            <div className="space-y-2 w-full">
              <Label htmlFor="approval-amount-value">Amount paid (UGX)</Label>
              <Input
                id="approval-amount-value"
                type="text"
                inputMode="numeric"
                value={amountValue}
                onChange={handleAmountInputChange}
                placeholder="e.g. 150,000"
                className="sm:max-w-[200px] w-full"
                disabled={isProcessing}
              />
            </div>
            {amountError ? <p className="text-sm text-destructive">{amountError}</p> : null}
            </div>
            <AlertDialogFooter className="sticky bottom-0 border-t bg-background/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
              <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprove}
                disabled={isProcessing || !durationIsValid || !amountIsValid}
              >
                {isProcessing ? 'Approving…' : 'Approve'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirming === 'reject'} onOpenChange={(open) => setConfirming(open ? 'reject' : null)}>
        <AlertDialogContent>
          <AlertDialogHeader className="space-y-2">
            <AlertDialogTitle>Decline application?</AlertDialogTitle>
            <AlertDialogDescription>
              The applicant will be marked as declined. Share the reason below so it&apos;s stored with their record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Reason for decline</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(event) => {
                  setRejectionReason(event.target.value)
                  if (rejectionError) {
                    setRejectionError(null)
                  }
                }}
                placeholder="Briefly explain why this application is being declined."
                disabled={isProcessing}
                rows={4}
              />
              {rejectionError ? <p className="text-sm text-destructive">{rejectionError}</p> : null}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={isProcessing || trimmedRejectionReason.length === 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isProcessing ? 'Declining…' : 'Decline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirming === 'correction'} onOpenChange={(open) => setConfirming(open ? 'correction' : null)}>
        <AlertDialogContent>
          <AlertDialogHeader className="space-y-2">
            <AlertDialogTitle>Request more information?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends correction notes to the applicant so they can update and re-submit the application.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="correction-comment">Correction notes</Label>
              <Textarea
                id="correction-comment"
                value={correctionComment}
                onChange={(event) => {
                  setCorrectionComment(event.target.value)
                  if (correctionError) {
                    setCorrectionError(null)
                  }
                }}
                placeholder="Explain what details or documents the applicant should update."
                disabled={isProcessing}
                rows={4}
              />
              {correctionError ? <p className="text-sm text-destructive">{correctionError}</p> : null}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRequestCorrection}
              disabled={isProcessing || trimmedCorrectionComment.length === 0}
            >
              {isProcessing ? 'Requesting…' : 'Send request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <FileViewerDialog
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
        fileUrl={selectedFile?.url}
        fileName={selectedFile?.name}
        description="View the uploaded document."
      />
    </>
  )
}

function DetailSection({
  title,
  description,
  children,
  className,
}: {
  title: string
  description?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={cn("space-y-3 rounded-lg border border-border bg-background p-4", className)}>
      <header className="space-y-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </header>
      <Separator />
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function DetailGrid({ rows }: { rows: { label: string; value: React.ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label} className="space-y-1 rounded-md border border-dashed border-muted/70 bg-muted/20 p-3">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{row.label}</dt>
          <dd className="text-sm text-foreground">{row.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  )
}

function SupplementaryInfo({ application }: { application: AdminApplication }) {
  const entries: { label: string; value: React.ReactNode }[] = []

  if (application.studentId || application.institution || application.graduationYear !== null) {
    entries.push({
      label: 'Student details',
      value: (
        <div className="space-y-1 text-sm">
          <div>Student ID: {application.studentId ?? '—'}</div>
          <div>Institution: {application.institution ?? '—'}</div>
          <div>Graduation year: {application.graduationYear ?? '—'}</div>
        </div>
      ),
    })
  }

  if (
    application.companyRegistrationNumber ||
    application.companySize ||
    application.businessSector ||
    application.yearsOfExperience !== null
  ) {
    entries.push({
      label: 'Organisation details',
      value: (
        <div className="space-y-1 text-sm">
          {/* <div>Registration number: {application.companyRegistrationNumber ?? '—'}</div> */}
          {/* <div>Company size: {application.companySize ?? '—'}</div> */}
          {/* <div>Business sector: {application.businessSector ?? '—'}</div> */}
          <div>Years of experience: {application.yearsOfExperience ?? '—'}</div>
        </div>
      ),
    })
  }

  if (application.professionalLicenseNumber) {
    entries.push({
      label: 'Professional license',
      value: application.professionalLicenseNumber,
    })
  }

  if (entries.length === 0) {
    return null
  }

  return (
    <DetailSection title="Additional information">
      <div className="grid gap-3">
        {entries.map((entry) => (
          <div key={entry.label} className="rounded-md border border-muted/60 bg-muted/10 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{entry.label}</p>
            <div className="mt-1 text-sm text-foreground">{entry.value ?? '—'}</div>
          </div>
        ))}
      </div>
    </DetailSection>
  )
}

function EmploymentHistorySection({ items }: { items: AdminApplication['employmentHistory'] }) {
  if (!items.length) return null
  return (
    <DetailSection title="Employment history">
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id ?? `${item.organisation ?? 'employment'}-${index}`} className="rounded-md border border-muted/60 bg-background p-3">
            <h3 className="text-sm font-semibold">{item.jobTitle ?? 'Role not specified'}</h3>
            <p className="text-sm text-muted-foreground">{item.organisation ?? 'Organisation not provided'}</p>
            <p className="text-xs text-muted-foreground">
              {formatDate(item.startDate) ?? 'Start date unknown'} — {formatDate(item.endDate) ?? (item.isCurrent ? 'Present' : 'Unknown')}
            </p>
            {item.keyResponsibilities ? (
              <p className="mt-2 text-sm text-foreground whitespace-pre-line">{item.keyResponsibilities}</p>
            ) : null}
          </div>
        ))}
      </div>
    </DetailSection>
  )
}

function EducationHistorySection({ items }: { items: AdminApplication['education'] }) {
  if (!items.length) return null
  return (
    <DetailSection title="Education history">
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id ?? index} className="rounded-md border border-muted/60 bg-background p-3">
            <h3 className="text-sm font-semibold">{item.qualification ?? 'Qualification not specified'}</h3>
            <p className="text-sm text-muted-foreground">{item.institution ?? 'Institution not provided'}</p>
            <p className="text-xs text-muted-foreground">
              {formatDate(item.startDate) ?? 'Start date unknown'} — {formatDate(item.endDate) ?? 'Completion date not provided'}
            </p>
            {item.fieldOfStudy ? (
              <p className="mt-2 text-sm text-foreground">Field of study: {item.fieldOfStudy}</p>
            ) : null}
          </div>
        ))}
      </div>
    </DetailSection>
  )
}

function AffiliationsSection({ items }: { items: AdminApplication['affiliations'] }) {
  if (!items.length) return null
  return (
    <DetailSection title="Professional affiliations">
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={`${item.organizationName ?? 'affiliation'}-${index}`} className="rounded-md border border-muted/60 bg-background p-3">
            <h3 className="text-sm font-semibold">{item.organizationName ?? 'Organisation not provided'}</h3>
            <p className="text-sm text-muted-foreground">
              Membership number: {item.membershipNumber ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground">
              Level: {item.membershipLevel ?? '—'} • Active: {item.isActive ? 'Yes' : 'No'}
            </p>
            <p className="text-xs text-muted-foreground">
              Joined {formatDate(item.joinDate) ?? '—'}{item.expiryDate ? ` • Expires ${formatDate(item.expiryDate) ?? '—'}` : ''}
            </p>
          </div>
        ))}
      </div>
    </DetailSection>
  )
}

function AwardsSection({ items }: { items: AdminApplication['awards'] }) {
  if (!items.length) return null
  return (
    <DetailSection title="Awards & recognitions">
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={`${item.awardName ?? 'award'}-${index}`} className="rounded-md border border-muted/60 bg-background p-3">
            <h3 className="text-sm font-semibold">{item.awardName ?? 'Award title not provided'}</h3>
            <p className="text-sm text-muted-foreground">
              Issued by {item.issuingOrganisation ?? '—'} on {formatDate(item.dateReceived) ?? '—'}
            </p>
            {item.description ? (
              <p className="mt-2 text-sm text-foreground whitespace-pre-line">{item.description}</p>
            ) : null}
          </div>
        ))}
      </div>
    </DetailSection>
  )
}

function DocumentsSection({ items, onViewFile }: { items: AdminApplication['documents']; onViewFile: (fileUrl: string, fileName: string) => void }) {
  const total = items.length
  const uploaded = items.filter((item) => Boolean(item.fileUrl)).length
  const awaiting = total - uploaded
  const pendingItems = items.filter((item) => !item.fileUrl)

  return (
    <DetailSection
      title="Supporting documents"
      description="Submitted files and outstanding requirements from the applicant."
      className="lg:sticky lg:top-6"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <DocumentStat label="Total" value={total} />
          <DocumentStat label="Uploaded" value={uploaded} tone="success" />
          <DocumentStat label="Missing" value={awaiting} tone={awaiting ? 'warning' : 'muted'} />
        </div>

        {total === 0 ? (
          <Alert>
            <AlertTitle>No files received</AlertTitle>
            <AlertDescription>
              There are no supporting documents attached to this application. Coordinate with the applicant to capture
              required items before approving.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="space-y-3">
              {items.map((item, index) => {
                const isUploaded = Boolean(item.fileUrl);
                return (
                  <div
                    key={item.fileId ?? `${item.documentType ?? 'document'}-${index}`}
                    className={cn(
                      'flex flex-col gap-2 rounded-md border bg-background p-3 shadow-sm transition hover:shadow-md',
                      isUploaded ? 'border-muted/60' : 'border-dashed border-amber-500/60 bg-amber-500/5'
                    )}
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <p className="text-sm font-medium leading-none">
                            {item.documentType ?? 'Document'}
                          </p>
                        </div>
                        <Badge variant={isUploaded ? 'outline' : 'destructive'}>
                          {isUploaded ? 'Uploaded' : 'Pending'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {item.fileName ?? item.fileUrl ?? 'No file reference available'}
                      </p>
                    </div>

                    {isUploaded ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewFile(item.fileUrl!, item.fileName ?? 'Document')}
                        >
                          View file
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-900 dark:text-amber-100">
                        <Info className="h-4 w-4" />
                        <span>Awaiting submission from applicant</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {pendingItems.length > 0 ? (
              <div className="rounded-md border border-dashed border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-50">
                {pendingItems.length === 1
                  ? `1 document still needs to be collected: ${pendingItems[0].documentType ?? 'Unknown type'}.`
                  : `${pendingItems.length} documents still need to be collected: ${pendingItems
                      .map((item) => item.documentType ?? 'Unknown')
                      .join(', ')}.`}
              </div>
            ) : null}
          </>
        )}
      </div>
    </DetailSection>
  );
}

function DocumentStat({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: number
  tone?: 'default' | 'success' | 'warning' | 'muted'
}) {
  const toneClasses = {
    default: 'bg-muted/40 text-foreground',
    success: 'bg-emerald-200/15 text-emerald-600 dark:text-emerald-300',
    warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-200',
    muted: 'bg-muted/20 text-muted-foreground',
  } as const

  return (
    <div className={cn('rounded-md border border-border/60 p-3 text-center', toneClasses[tone])}>
      <p className="text-xs font-medium uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
